/** QuickRouter — Fichiers de perçage Excellon (métriques, coordonnées décimales absolues).
 *
 * Deux fichiers comme KiCad : PTH (pastilles métallisées + vias) et NPTH (trous de
 * fixation non métallisés). Outils regroupés par diamètre au micron près.
 * Les trous oblongs (fentes) n'existent pas dans le modèle v0.1 : aucun n'est émis.
 */
import { worldPads } from '../geom.js';

const f3 = (v) => {
  if (!Number.isFinite(v)) throw new Error(`coordonnée de perçage invalide : ${v}`);
  return v.toFixed(3);
};

/** Trous de la carte : { pth: [{ pos, drill, what }], npth: [...] }. */
export function collectHoles(project, lookup) {
  const pth = [], npth = [];
  for (const c of project.components) {
    const fp = lookup(c.footprint);
    for (const p of worldPads(c, fp)) {
      if (!p.drill) continue;
      (p.plated ? pth : npth).push({ pos: p.pos, drill: p.drill, what: p.key });
    }
  }
  for (const v of project.vias) pth.push({ pos: v.pos, drill: v.drill, what: `via ${v.id}` });
  return { pth, npth };
}

export function excellon(holes, { plated = true } = {}) {
  const tools = new Map(); // diamètre arrondi → numéro
  const byTool = new Map();
  for (const h of holes) {
    const d = Math.round(h.drill * 1000) / 1000;
    if (!tools.has(d)) tools.set(d, tools.size + 1);
    const t = tools.get(d);
    if (!byTool.has(t)) byTool.set(t, []);
    byTool.get(t).push(h.pos);
  }
  const out = [
    'M48',
    '; DRILL file {QuickRouter} — QuickApps',
    `; FORMAT={-:-/ absolute / metric / decimal}`,
    `; #@! TF.FileFunction,${plated ? 'Plated,1,2,PTH' : 'NonPlated,1,2,NPTH'}`,
    `; TYPE=${plated ? 'PLATED' : 'NON_PLATED'}`,
    'FMAT,2',
    'METRIC',
    ...[...tools.entries()].map(([d, t]) => `T${t}C${d.toFixed(3)}`),
    '%',
    'G90',
    'G05',
  ];
  for (const [t, pts] of byTool) {
    out.push(`T${t}`);
    for (const p of pts) out.push(`X${f3(p[0])}Y${f3(p[1])}`);
  }
  out.push('M30');
  return `${out.join('\n')}\n`;
}

/** Les deux fichiers + avertissements (trous sous le perçage minimal). */
export function drillFiles(project, lookup) {
  const { pth, npth } = collectHoles(project, lookup);
  const warnings = [];
  const small = [...pth, ...npth].filter((h) => h.drill < project.rules.minDrill - 1e-9);
  if (small.length) warnings.push(`${small.length} trou(s) sous le perçage minimal de ${project.rules.minDrill} mm (${small.slice(0, 5).map((h) => h.what).join(', ')})`);
  return { pth: excellon(pth, { plated: true }), npth: excellon(npth, { plated: false }), counts: { pth: pth.length, npth: npth.length }, warnings };
}
